import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { useMe } from "./MeContext";
import { Issue } from "@/api/types";
import { OrderApi } from "@/api/order.api";
import { pdf } from "@react-pdf/renderer";
import Receipt from "@/components/User/Shared/Receipt/Receipt";

interface CartItem extends Partial<Issue> {
  id: number;
  price: string;
  title: string;
  cartId: string;
  type: "subscription" | "issue" | "publication";
  included?: boolean;
}
interface CartContextType {
  cart: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (cartId: string) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { me } = useMe();
  const myId = me?.id ?? "";
  const storageKey = `cart_${myId}`;

  const [cart, setCart] = useState<CartItem[]>(() => {
    const savedCart = localStorage.getItem(storageKey);
    return savedCart ? JSON.parse(savedCart) : [];
  });

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(cart));
  }, [cart, myId]);

  useEffect(() => {
    const pendingOrderId = localStorage.getItem("orderId");
    if (!pendingOrderId || !cart.length) return;

    const checkIfPaid = async () => {
      const status = await OrderApi.getOrderStatus(parseInt(pendingOrderId));

      if (status === "COMPLETED") {
        const order = await OrderApi.getOrderData(Number(pendingOrderId));
        if (order) {
          const blob = await pdf(<Receipt order={order} />).toBlob();
          const base64 = await blobToBase64(blob);

          const email = order.user.email;

          if (email) {
            await OrderApi.sendReceipt(email, order.id, base64);
          }
        }
        clearCart();
        localStorage.removeItem("orderId");
      }
    };

    checkIfPaid();
  }, []);

  const addToCart = (item: CartItem) => {
    setCart((prevCart) => {
      return [...prevCart, item];
    });
  };

  const removeFromCart = (cartId: string) => {
    setCart((prevCart) => prevCart.filter((item) => item.cartId !== cartId));
  };

  const clearCart = () => {
    setCart([]);
    if (myId) localStorage.removeItem(storageKey);
  };

  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  return (
    <CartContext.Provider
      value={{ cart, addToCart, removeFromCart, clearCart }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
